import React from "react";

// Subtle roof-shingle pattern as background - instant visual identity
export function ShinglePattern({ className = "", color = "currentColor" }) {
  return (
    <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="shingle" x="0" y="0" width="80" height="40" patternUnits="userSpaceOnUse">
          <path
            d="M 0 30 L 20 10 L 40 30 L 60 10 L 80 30 L 80 40 L 0 40 Z M 0 0 L 0 10 L 20 -10 L 40 10 L 60 -10 L 80 10 L 80 0 Z"
            fill={color}
            opacity="0.08"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#shingle)" />
    </svg>
  );
}

// Roof silhouette divider - used between sections as decorative accent
export function RoofDivider({ className = "", flip = false, color = "#FAFAFA" }) {
  return (
    <svg
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      className={`block w-full h-12 md:h-16 ${className}`}
      style={{ transform: flip ? "scaleY(-1)" : undefined }}
      aria-hidden="true"
    >
      <path
        d="M0,80 L0,40 L120,10 L240,40 L360,10 L480,40 L600,10 L720,40 L840,10 L960,40 L1080,10 L1200,40 L1320,10 L1440,40 L1440,80 Z"
        fill={color}
      />
    </svg>
  );
}

// Animated roof line - used as decorative element near hero
export function RoofLineAccent({ className = "" }) {
  return (
    <svg viewBox="0 0 200 40" className={className} aria-hidden="true">
      <path
        d="M0,32 L20,16 L40,32 L60,16 L80,32 L100,16 L120,32 L140,16 L160,32 L180,16 L200,32"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

// House silhouette icon - for AI Q&A blocks
export function HouseIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 48" className={className} fill="currentColor" aria-hidden="true">
      <path d="M32 4 L4 24 L4 26 L8 26 L8 44 L26 44 L26 30 L38 30 L38 44 L56 44 L56 26 L60 26 L60 24 Z" />
    </svg>
  );
}

// ----------------------------------------
// Decorative corner accents for tiles. Varied by "variant" prop so adjacent
// cards never look identical and the page feels lively.
// Each is an inline SVG absolutely-positioned in the card's bottom-right
// (or top-right for variant="topRight") at low opacity.
// ----------------------------------------

// Variant A: simple gable roof (triangle)
function AccentGable({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M12 58 L40 24 L68 58" />
      <path d="M18 58 L18 72 L62 72 L62 58" />
    </svg>
  );
}

// Variant B: house with window
function AccentHouseWindow({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M12 42 L40 18 L68 42" />
      <path d="M20 42 L20 70 L60 70 L60 42" />
      <rect x="34" y="50" width="12" height="14" />
      <path d="M34 57 L46 57 M40 50 L40 64" />
    </svg>
  );
}

// Variant C: house with door + chimney
function AccentHouseDoor({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M12 42 L40 18 L68 42" />
      <path d="M54 24 L54 14 L60 14 L60 28" />
      <path d="M20 42 L20 70 L60 70 L60 42" />
      <rect x="36" y="52" width="10" height="18" />
    </svg>
  );
}

// Variant D: shingle rows (3 overlapping ridges)
function AccentShingles({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M6 60 L20 46 L34 60 L48 46 L62 60 L74 46" />
      <path d="M6 50 L20 36 L34 50 L48 36 L62 50 L74 36" />
      <path d="M6 40 L20 26 L34 40 L48 26 L62 40 L74 26" />
    </svg>
  );
}

// Variant E: weather vane / cupola on a roof - distinctive and fun
function AccentWeatherVane({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M10 60 L40 30 L70 60" />
      <path d="M40 30 L40 12" />
      <path d="M36 16 L44 16" />
      <path d="M32 8 L48 8 L48 12 L32 12 Z" />
    </svg>
  );
}

// Variant F: row of little houses along a skyline
function AccentSkyline({ className }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 100 60" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M4 50 L10 42 L16 50 L16 58 L4 58 Z" />
      <path d="M20 50 L30 38 L40 50 L40 58 L20 58 Z" />
      <path d="M44 50 L52 42 L60 50 L60 58 L44 58 Z" />
      <path d="M64 50 L76 38 L88 50 L88 58 L64 58 Z" />
    </svg>
  );
}

const variants = {
  gable: AccentGable,
  window: AccentHouseWindow,
  door: AccentHouseDoor,
  shingles: AccentShingles,
  vane: AccentWeatherVane,
  skyline: AccentSkyline,
};

/**
 * Decorative SVG corner accent for cards. Use with `<RoofAccent variant="window" />`.
 * Defaults: bottom-right, 80px, 8% opacity. Appears only on hover by default.
 * For dark-themed cards (black/dark bg), pass `onDark` - starts at subtle gray,
 * intensifies toward white on hover.
 */
export function RoofAccent({ variant = "gable", position = "bottom-right", size = "md", intensity = "soft", hoverOnly = true, onDark = false }) {
  const Comp = variants[variant] || variants.gable;
  const sizes = { sm: "w-14 h-14", md: "w-20 h-20", lg: "w-28 h-28", xl: "w-40 h-40" };
  const pos = {
    "bottom-right": "bottom-0 right-0 translate-x-3 translate-y-3",
    "top-right": "top-0 right-0 translate-x-2 -translate-y-2",
    "bottom-left": "bottom-0 left-0 -translate-x-3 translate-y-3",
    "center-right": "top-1/2 right-0 -translate-y-1/2 translate-x-2",
  };
  // Light backgrounds: low-opacity ink lines, visible only on hover by default.
  const opLight = {
    soft: hoverOnly ? "opacity-0 group-hover:opacity-[0.09]" : "opacity-[0.06]",
    medium: hoverOnly ? "opacity-0 group-hover:opacity-[0.14]" : "opacity-[0.1]",
    bold: hoverOnly ? "opacity-0 group-hover:opacity-[0.2]" : "opacity-[0.15]",
  };
  // Dark backgrounds: start at soft gray, grow brighter on hover (almost white).
  // currentColor inherits from parent - make sure parent has text-white or similar.
  const opDark = {
    soft: "opacity-[0.10] group-hover:opacity-[0.28]",
    medium: "opacity-[0.14] group-hover:opacity-[0.38]",
    bold: "opacity-[0.2] group-hover:opacity-[0.55]",
  };
  const opCls = onDark ? opDark[intensity] : opLight[intensity];
  return (
    <Comp className={`absolute ${pos[position]} ${sizes[size]} ${opCls} pointer-events-none transition-all duration-500`} />
  );
}

/**
 * Decorative ambient accent for dark hero sections. Drop this immediately
 * inside a dark `<section class="... relative overflow-hidden group">`.
 * Subtle at rest (~4-5% opacity), brightens on section hover (~12-14%).
 * Varies by the `tone` prop: "ridgeline" | "skyline" | "shingles" | "house".
 */
export function DarkHeroAccent({ tone = "ridgeline" }) {
  if (tone === "skyline") {
    return (
      <svg aria-hidden="true" className="absolute -right-12 bottom-0 w-[460px] h-[180px] opacity-[0.05] group-hover:opacity-[0.14] transition-opacity duration-700 pointer-events-none" viewBox="0 0 460 180" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M20 160 L60 120 L100 160 L100 180 L20 180 Z" />
        <path d="M120 160 L170 100 L220 160 L220 180 L120 180 Z" />
        <path d="M240 160 L280 130 L320 160 L320 180 L240 180 Z" />
        <path d="M340 160 L400 100 L460 160 L460 180 L340 180 Z" />
        <rect x="165" y="140" width="12" height="16" />
        <rect x="395" y="140" width="12" height="16" />
      </svg>
    );
  }
  if (tone === "shingles") {
    return (
      <svg aria-hidden="true" className="absolute -right-20 top-10 w-[560px] h-[280px] opacity-[0.05] group-hover:opacity-[0.13] transition-opacity duration-700 pointer-events-none" viewBox="0 0 560 280" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M20 230 L90 170 L160 230 L230 170 L300 230 L370 170 L440 230 L510 170" />
        <path d="M20 200 L90 140 L160 200 L230 140 L300 200 L370 140 L440 200 L510 140" />
        <path d="M20 170 L90 110 L160 170 L230 110 L300 170 L370 110 L440 170 L510 110" />
        <path d="M20 140 L90 80 L160 140 L230 80 L300 140 L370 80 L440 140 L510 80" />
      </svg>
    );
  }
  if (tone === "house") {
    return (
      <svg aria-hidden="true" className="absolute -right-10 top-6 w-[420px] h-[220px] opacity-[0.05] group-hover:opacity-[0.14] transition-opacity duration-700 pointer-events-none" viewBox="0 0 420 220" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M40 180 L130 80 L220 180" />
        <path d="M60 180 L60 210 L200 210 L200 180" />
        <rect x="115" y="150" width="30" height="40" />
        <path d="M240 180 L300 120 L360 180 L360 210 L240 210 Z" />
        <rect x="275" y="150" width="20" height="30" />
      </svg>
    );
  }
  // Default "ridgeline" - wavy overlapping ridges
  return (
    <svg aria-hidden="true" className="absolute -right-16 top-8 w-[520px] h-[260px] opacity-[0.05] group-hover:opacity-[0.12] transition-opacity duration-700 pointer-events-none" viewBox="0 0 520 260" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M40 200 L140 100 L240 200 L340 80 L440 200 L500 140" />
      <path d="M60 200 L60 230 L460 230 L460 200" />
      <rect x="190" y="160" width="80" height="70" />
      <path d="M190 190 L270 190 M230 160 L230 230" />
      <path d="M380 80 L380 60 L400 60 L400 100" />
    </svg>
  );
}
export function CardNumberStamp({ n, className = "" }) {
  return (
    <div aria-hidden="true" className={`font-heading font-extrabold text-5xl md:text-6xl text-brand/20 tracking-tight leading-none select-none ${className}`}>
      {String(n).padStart(2, "0")}
    </div>
  );
}

// Horizontal scroll-snap container for mobile carousels (desktop = grid)
export function HScroll({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`flex md:grid overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-6 md:mx-0 px-6 md:px-0 pb-4 md:pb-0 gap-4 md:gap-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
    >
      {children}
    </div>
  );
}

export function HScrollItem({ children, className = "" }) {
  return (
    <div className={`snap-start shrink-0 w-[82%] sm:w-[60%] md:w-auto ${className}`}>
      {children}
    </div>
  );
}
